import { gql, QueryResult } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import Button from '../../../common/src/components/Button'
import { Link } from '../../../common/src/components/Link'
import Table from '../../../common/src/components/Table'
import { H1, Spacer, H3, BodyTextSpan, BodyMonoText, ColoredSpan } from '../../../common/src/components/core'
import { Colors } from '../../../common/src/constants'
import { isoSecondsFmt, dateMDTimeFmt, dateTimeSecTzFmt } from '../../../common/src/date'
import { getFlag } from '../../../common/src/flags'
import {
  MessageFirehoseQueryQuery,
  CountdownFragment,
  MessageLogFragment,
  TaskFirmDispFragment,
} from '../../../common/src/types.gen'
import { Fragments } from '../graphql/fragments'
import { PollingQuery } from './components/PollingQuery'
import { getContactText } from './components/contact'
import { getHash, getTaskPath, getThreadPath, getThreadPathFor } from './route'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function FirehosePage(props: RouteComponentProps) {
  return (
    <div style={{ padding: 32, minWidth: getFlag('firehoseDemoMode') ? 736 : 1200 }}>
      <H1>Lisa monitor</H1>
      <Spacer height={32} />
      <MessageFirehose />
    </div>
  )
}

const messageFirehoseQuery = gql`
  query MessageFirehoseQuery {
    countdowns(n: 8) {
      ...Countdown
    }
    messages(n: 80) {
      ...MessageLog
    }
  }
  fragment Countdown on BotThread {
    sendTime
    contact {
      ...Contact
    }
    task {
      ...TaskFirmDisp
    }
    action {
      classification
      preview
    }
    actionMessageType
  }
  fragment MessageLog on Message {
    id
    timeCreated
    isInbound
    addressFrom
    addressTo
    messageType
    classification
    actionUrl
    shortBody
    thread {
      id
      contactThem {
        ...Contact
      }
    }
    task {
      ...TaskFirmDisp
    }
    author {
      id
      name
    }
  }
  fragment TaskFirmDisp on LeasingTask {
    id
    isArchived
    isBotDisabled
    launchDate
    offboardDate
    displayName
    firm {
      id
      firmName
    }
  }
  ${Fragments.contact}
`

enum TZ {
  UTC = 'UTC',
  EASTERN = 'EASTERN',
  PACIFIC = 'PACIFIC',
}

class MessageFirehoseImpl extends React.PureComponent<
  QueryResult<MessageFirehoseQueryQuery>,
  { timeLag: Date; tzName: TZ }
> {
  private interval = 0
  state = {
    timeLag: getLaggedTime(),
    tzName: TZ.UTC,
  }

  componentDidMount() {
    this.interval = window.setInterval(this.tick, 100)
  }

  componentWillUnmount() {
    window.clearInterval(this.interval)
  }

  render() {
    const { data } = this.props
    const { timeLag, tzName } = this.state
    if (!data || !data.messages || !data.countdowns) {
      return null
    }

    // Show countdowns in right order
    const countdowns = data.countdowns.slice().reverse()

    // If multiple messages happened in the same second, spread them out
    const iso = timeLag.toISOString() // eg 2019-10-10T04:20:00.430
    const laggedMsgs = []
    let numEq = 9
    for (const msg of data.messages) {
      let tcTrim = msg.timeCreated.substring(0, 19)
      if (iso.startsWith(tcTrim)) {
        tcTrim += '.' + numEq
        numEq -= 2
      }
      if (tcTrim < iso) {
        laggedMsgs.push(msg)
      }
    }

    const countColTime = (c: CountdownFragment) => {
      const text = c.sendTime ? getCountdownHMS(new Date(c.sendTime), this.state.timeLag) : ''
      return <MonoTrunc color={text.startsWith('T - 00:00:') ? 'mint' : 'silver'}>{text}</MonoTrunc>
    }

    const countdownCols = [countColTime].concat(this.countdownColsNoTime)
    return (
      <div>
        {countdowns.length && (
          <div>
            <H3>
              Countdowns
              <Spacer width={16} />
              <BodyTextSpan>Pending outbound bot messages</BodyTextSpan>
            </H3>
            <Spacer height={16} />
            <Table
              keyFn={this.countdownIdFunc}
              rows={countdowns}
              columns={countdownCols}
              headers={this.countdownHeaders}
              widths={this.widths}
              dataRowComponent={MsgRow}
            />
            <Spacer height={32} />
          </div>
        )}
        <div>
          <H3>
            Latest messages
            <Spacer width={16} />
            <BodyMonoText as="span">
              {isoSecondsFmt(timeLag)}
              <Spacer width={8} />
              <Button size="small" disabled={tzName === TZ.UTC} onClick={this.setTzUTC}>
                UTC
              </Button>
              <Spacer width={16} />
              {dateMDTimeFmt(timeLag, 'America/New_York')}
              <Spacer width={8} />
              <Button size="small" disabled={tzName === TZ.EASTERN} onClick={this.setTzEastern}>
                EDT
              </Button>
              <Spacer width={16} />
              {dateMDTimeFmt(timeLag, 'America/Los_Angeles')}
              <Spacer width={8} />
              <Button size="small" disabled={tzName === TZ.PACIFIC} onClick={this.setTzPacific}>
                PDT
              </Button>
            </BodyMonoText>
          </H3>
          <Spacer height={16} />
          <Table
            keyFn={this.msgIdFunc}
            rows={laggedMsgs}
            columns={this.colsByTz[tzName]}
            headers={this.headersByTz[tzName]}
            widths={this.widths}
            dataRowComponent={MsgRow}
          />
        </div>
      </div>
    )
  }

  private tick = () => this.setState({ timeLag: getLaggedTime() })

  private setTzUTC = () => this.setState({ tzName: TZ.UTC })
  private setTzEastern = () => this.setState({ tzName: TZ.EASTERN })
  private setTzPacific = () => this.setState({ tzName: TZ.PACIFIC })

  private countdownIdFunc = (c: CountdownFragment) => c.task.id + '-' + c.contact.id

  private colCFrom = () => <Trunc color="mint">Lisa · Bot</Trunc>
  private colCTo = (c: CountdownFragment) => <ContactElem countdown={c} />
  private colCType = (c: CountdownFragment) => <MonoTrunc>{c.actionMessageType}</MonoTrunc>
  private colCClass = (c: CountdownFragment) => (
    <MonoTrunc color="mint">{c.action && c.action.classification}</MonoTrunc>
  )
  private colCTask = (c: CountdownFragment) => <TaskElem task={c.task} />
  private colCFirm = (c: CountdownFragment) => <FirmElem task={c.task} />
  private colCBody = (c: CountdownFragment) => <Trunc>{c.action && c.action.preview}</Trunc>

  private msgIdFunc = (msg: MessageLogFragment) => msg.id

  private colFrom = (msg: MessageLogFragment) =>
    msg.isInbound ? <ContactElem msg={msg} /> : <LisaElem msg={msg} isDemoMode={this.isDemoMode} />
  private colTo = (msg: MessageLogFragment) => (msg.isInbound ? <Trunc>Lisa</Trunc> : <ContactElem msg={msg} />)
  private colType = (msg: MessageLogFragment) => <MonoTrunc>{msg.messageType}</MonoTrunc>
  private colClass = (msg: MessageLogFragment) => <ClassificationElem msg={msg} />
  private colTask = (msg: MessageLogFragment) => <TaskElem task={msg.task} />
  private colFirm = (msg: MessageLogFragment) => <FirmElem task={msg.task} />
  private colBody = (msg: MessageLogFragment) => (
    <Trunc>
      <Link tableCell to={getThreadPath(msg.thread.id) + getHash('message', msg.id)} color="silver">
        {msg.shortBody}
      </Link>
    </Trunc>
  )

  private isDemoMode = getFlag('firehoseDemoMode')

  private countdownHeaders = this.isDemoMode
    ? ['Time', 'From', 'To', 'Message']
    : ['Time', 'From', 'To', 'Type', 'Classification', 'Task', 'Firm', 'Message']
  private countdownColsNoTime = this.isDemoMode
    ? [this.colCFrom, this.colCTo, this.colCBody]
    : [this.colCFrom, this.colCTo, this.colCType, this.colCClass, this.colCTask, this.colCFirm, this.colCBody]

  private headersNoTime = this.isDemoMode
    ? ['From', 'To', 'Message']
    : ['From', 'To', 'Type', 'Classification', 'Task', 'Firm', 'Message']

  private colsNoTime = this.isDemoMode
    ? [this.colFrom, this.colTo, this.colBody]
    : [this.colFrom, this.colTo, this.colType, this.colClass, this.colTask, this.colFirm, this.colBody]

  private colTimeGen = (tz: TZ) => (msg: MessageLogFragment) => {
    const tzName = tz === 'PACIFIC' ? 'America/Los_Angeles' : tz === 'EASTERN' ? 'America/New_York' : 'UTC'
    const time = tz === TZ.UTC ? msg.timeCreated : dateTimeSecTzFmt(msg.timeCreated, tzName)
    return <MonoTrunc>{time}</MonoTrunc>
  }

  private colsByTz = {
    UTC: [this.colTimeGen(TZ.UTC)].concat(this.colsNoTime),
    PACIFIC: [this.colTimeGen(TZ.PACIFIC)].concat(this.colsNoTime),
    EASTERN: [this.colTimeGen(TZ.EASTERN)].concat(this.colsNoTime),
  }

  private headersByTz = {
    UTC: ['Time UTC'].concat(this.headersNoTime),
    PACIFIC: ['Time Pacific'].concat(this.headersNoTime),
    EASTERN: ['Time Eastern'].concat(this.headersNoTime),
  }

  private widths = this.isDemoMode
    ? ['184px', '120px', '120px', null]
    : ['184px', '120px', '120px', '60px', '120px', '120px', '80px', null]
}

function getLaggedTime(): Date {
  const msLagged = new Date().getTime() - 1000 * 6
  return new Date(msLagged)
}

function LisaElem({ msg, isDemoMode }: { msg: MessageLogFragment; isDemoMode: boolean }) {
  const color = msg.author && !isDemoMode ? 'sky' : 'mint'
  let colorText
  let grayText
  if (msg.author) {
    colorText = 'Lisa'
    grayText = ' · ' + msg.author.name
  } else {
    colorText = 'Lisa · Bot'
  }
  return (
    <Trunc>
      <ColoredSpan color={color}>{colorText}</ColoredSpan>
      {grayText}
    </Trunc>
  )
}

const unhighlightedInboundClasses = ['HUMAN_FALLBACK', 'NO_QUERY', 'EMPTY_MESSAGE']
function ClassificationElem({ msg }: { msg: MessageLogFragment }) {
  const { isInbound } = msg
  let cl = msg.classification
  if (!cl) {
    return null
  }
  const color = isInbound && !unhighlightedInboundClasses.includes(cl) ? 'mint' : undefined
  if (cl === 'EMPTY_MESSAGE' && msg.messageType === 'VOICE') {
    cl = 'INBOUND_CALL'
  }
  return <MonoTrunc color={color}>{cl}</MonoTrunc>
}

function ContactElem({ msg, countdown }: { msg?: MessageLogFragment; countdown?: CountdownFragment }) {
  let contact
  let threadUrl
  if (countdown) {
    contact = countdown.contact
    threadUrl = getThreadPathFor({ contactId: countdown.contact.id, taskId: countdown.task.id })
  } else if (msg) {
    contact = msg.thread.contactThem
    threadUrl = getThreadPath(msg.thread.id)
  } else {
    return null
  }
  const text = getContactText(contact)
  return (
    <Trunc>
      <Link tableCell color="silver" to={threadUrl}>
        {text}
      </Link>
    </Trunc>
  )
}

function TaskElem({ task }: { task: MessageLogFragment['task'] | TaskFirmDispFragment | null }) {
  return (
    <Trunc>
      {task ? (
        <Link tableCell to={getTaskPath(task.id)} color={task.isArchived || task.isBotDisabled ? 'coral' : 'silver'}>
          {task.displayName}
        </Link>
      ) : (
        <ColoredSpan color="coral">No task</ColoredSpan>
      )}
    </Trunc>
  )
}

function FirmElem({ task }: { task: MessageLogFragment['task'] | TaskFirmDispFragment | null }) {
  return <Trunc>{task ? task.firm.firmName : ''}</Trunc>
}

/**
 * Returns eg "T - 00:01:14" or "NOW"
 */
export function getCountdownHMS(t: Date, now: Date) {
  const secs = Math.floor((t.getTime() - now.getTime()) / 1000)
  if (secs < 0) {
    return 'NOW'
  }
  const s = secs % 60
  const m = Math.floor(secs / 60) % 60
  const h = Math.floor(secs / 3600)
  return 'T - ' + fmt00(h) + ':' + fmt00(m) + ':' + fmt00(s)
}

export function fmt00(n: number) {
  if (n > 9) {
    return '' + n
  }
  return '0' + n
}

const MsgRow = styled.tr`
  transition: background-color 0.05s ease-in-out;
  &:hover {
    background: ${Colors.black};
  }
`

const Trunc = BodyTextSpan
const MonoTrunc = BodyMonoText

function MessageFirehose() {
  return (
    <PollingQuery query={messageFirehoseQuery}>
      {(res: QueryResult<MessageFirehoseQueryQuery>) => <MessageFirehoseImpl {...res} />}
    </PollingQuery>
  )
}
